import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { JsonLd, getBreadcrumbSchema, getWebPageSchema, getArticleSchema } from '@/components/feature/JsonLd';
import { videos, categories } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const videoId = parseInt(id || '0', 10);
  const video = videos.find((v) => v.id === videoId);

  if (!video) {
    return (
      <>
        <ScrollToTop />
        <Header />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-900 mb-2">视频不存在</h2>
            <p className="text-foreground-500 mb-6">该视频可能已被删除或链接无效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === video.categorySlug);
  const relatedVideos = videos
    .filter((v) => v.categorySlug === video.categorySlug && v.id !== video.id)
    .slice(0, 5);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: video.category, href: `/category/${video.categorySlug}` },
    { label: video.title },
  ];

  const pageTitle = `${video.title} - 桃花视频 | 高清在线观看`;
  const canonicalUrl = `/article/${video.id}`;

  return (
    <>
      <ScrollToTop />
      <JsonLd data={getWebPageSchema(pageTitle, video.description, canonicalUrl)} />
      <JsonLd data={getArticleSchema({
        title: video.title,
        description: video.description,
        thumbnail: video.thumbnail,
        releaseDate: video.releaseDate,
        url: canonicalUrl,
        category: video.category,
      })} />
      <JsonLd data={getBreadcrumbSchema([
        { name: '桃花视频', item: '/' },
        { name: video.category, item: `/category/${video.categorySlug}` },
        { name: video.title, item: canonicalUrl },
      ])} />

      <Header />

      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <article className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-50" aria-labelledby="video-title">
          <div className="max-w-5xl mx-auto">
            <div className="aspect-video rounded-lg overflow-hidden bg-black mb-6 relative">
              <img
                src={video.thumbnail}
                alt={video.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 flex items-center justify-center cursor-pointer hover:bg-white hover:scale-105 transition-all duration-200">
                  <i className="ri-play-fill text-3xl md:text-4xl text-primary-500 ml-1"></i>
                </div>
              </div>
              <span className="absolute bottom-4 right-4 px-3 py-1 text-sm font-medium rounded bg-black/70 text-white">
                {video.duration}
              </span>
            </div>

            <header>
              <h1 id="video-title" className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                {video.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 md:gap-5 text-sm text-foreground-500 mb-4">
                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-background-100">
                  <i className="ri-eye-line"></i>
                  {video.views >= 10000 ? `${(video.views / 10000).toFixed(0)}万次播放` : `${video.views}次播放`}
                </span>
                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-background-100">
                  <i className="ri-star-fill text-accent-500"></i>
                  {video.rating} 分
                </span>
                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-background-100">
                  <i className="ri-calendar-line"></i>
                  {video.releaseDate}
                </span>
                <Link
                  to={`/category/${video.categorySlug}`}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-600 hover:bg-primary-200 transition-colors duration-200 cursor-pointer"
                >
                  <i className="ri-price-tag-3-line"></i>
                  {video.category}
                </Link>
              </div>
            </header>

            <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed mb-8">
              <p className="text-base">{video.description}</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {video.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-background-200">
              <button className="flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors duration-200 cursor-pointer">
                <i className="ri-thumb-up-line"></i>
                赞
              </button>
              <button className="flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors duration-200 cursor-pointer">
                <i className="ri-bookmark-line"></i>
                收藏
              </button>
              <button className="flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors duration-200 cursor-pointer">
                <i className="ri-share-forward-line"></i>
                分享
              </button>
            </div>
          </div>
        </article>

        {relatedVideos.length > 0 && (
          <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100" aria-label="相关推荐">
            <div className="max-w-5xl mx-auto">
              <div className="mb-5 md:mb-6">
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900">
                  相关推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">
                  更多{video.category}类精彩内容
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                {relatedVideos.map((v) => (
                  <VideoCard key={v.id} video={v} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}